// Customers list — M2 Phase 5.2 surface. Reads /customers via crm.ts.
//
// Features:
//   - Search box (debounced 200ms) → searchCustomers
//   - Tier filter chips (All / VIP / Good / Slow Pay / Bad / New)
//   - Responsive card grid (sm:1 / md:2 / lg:3 col)
//   - Click card → /customers/:id
//   - "+ New customer" → modal → navigates to new customer on create
//   - Empty / loading / error states matched to RemindersPanel style

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listCustomers, searchCustomers } from '@/shared/crm';
import type { Customer, CustomerTier } from '@/shared/crmTypes';
import { TierBadge } from '@/components/TierBadge';
import { NewCustomerModal } from '@/components/NewCustomerModal';

type TierFilter = CustomerTier | 'all';

const TIER_FILTERS: Array<{ key: TierFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'vip', label: 'VIP' },
  { key: 'good', label: 'Good' },
  { key: 'slow_pay', label: 'Slow Pay' },
  { key: 'bad', label: 'Bad' },
  { key: 'new', label: 'New' },
];

function fmtRelativeDays(iso?: string): string | null {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'in the future';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function pickPrimary(c: Customer) {
  return c.contacts?.find((x) => x.primary) ?? c.contacts?.[0];
}

export function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [tierFilter, setTierFilter] = useState<TierFilter>('all');
  const [modalOpen, setModalOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = searchQuery.trim() ? await searchCustomers(searchQuery) : await listCustomers();
      setCustomers(data);
    } catch (e) {
      setError(`Failed to load customers: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Debounce search input → searchQuery
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const filtered = useMemo(() => {
    if (tierFilter === 'all') return customers;
    return customers.filter((c) => c.tier === tierFilter);
  }, [customers, tierFilter]);

  function handleCardClick(c: Customer) {
    navigate(`/customers/${c.id}`);
  }

  function handleCreated(id: string) {
    setModalOpen(false);
    navigate(`/customers/${id}`);
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-navy">Customers</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/customers/bulk-tier')}
            className="px-3 py-1.5 bg-paper border border-line-dark text-text text-sm rounded font-semibold hover:border-orange hover:text-orange transition-colors"
          >
            Bulk tier
          </button>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 transition-opacity"
          >
            + New customer
          </button>
        </div>
      </div>

      {/* Search + filter row */}
      <div className="bg-paper border border-line rounded p-3 mb-4">
        <div className="flex gap-2 items-center mb-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by company name or code…"
            className="flex-1 px-3 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="text-xs text-text-mute hover:text-orange whitespace-nowrap px-2"
          >
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TIER_FILTERS.map((t) => {
            const active = tierFilter === t.key;
            const count =
              t.key === 'all'
                ? customers.length
                : customers.filter((c) => c.tier === t.key).length;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTierFilter(t.key)}
                className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${
                  active
                    ? 'bg-orange text-white'
                    : 'bg-bg text-text-mute border border-line-dark hover:border-orange hover:text-orange'
                }`}
              >
                {t.label}
                <span className="ml-1 opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm mb-4 flex items-center justify-between gap-3">
          <span>⚠ {error}</span>
          <button
            type="button"
            onClick={refresh}
            className="text-xs text-orange font-semibold hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {loading && customers.length === 0 && (
        <div className="bg-paper border border-line rounded p-8 text-center text-text-mute">
          <div className="animate-pulse">Loading customers…</div>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && customers.length === 0 && (
        <div className="bg-paper border border-line rounded p-8 text-center">
          <div className="text-4xl mb-2">📇</div>
          <div className="font-semibold text-navy mb-1">No customers yet</div>
          <p className="text-sm text-text-mute mb-4">
            Add your first customer to start tracking quotes, orders, and tier history.
          </p>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
          >
            + Create your first customer
          </button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && customers.length > 0 && (
        <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
          <div className="text-sm">
            No customers match{' '}
            {searchQuery.trim() ? (
              <span className="font-mono">"{searchQuery}"</span>
            ) : (
              <span>this filter</span>
            )}
            .
          </div>
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              setTierFilter('all');
            }}
            className="text-xs text-orange font-semibold hover:underline mt-2"
          >
            Clear filters
          </button>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((c) => (
            <CustomerCard key={c.id} customer={c} onClick={() => handleCardClick(c)} />
          ))}
        </div>
      )}

      <NewCustomerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}

interface CustomerCardProps {
  customer: Customer;
  onClick: () => void;
}

function CustomerCard({ customer, onClick }: CustomerCardProps) {
  const primary = pickPrimary(customer);
  const lastContact = fmtRelativeDays(customer.lastContactDate);

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-paper border border-line rounded p-4 hover:border-orange hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-navy text-lg truncate" title={customer.companyName}>
            {customer.companyName}
          </div>
          {customer.code && (
            <div className="text-xs font-mono text-text-mute">{customer.code}</div>
          )}
        </div>
        <TierBadge tier={customer.tier} size="sm" />
      </div>

      {primary && (
        <div className="text-xs space-y-0.5 mt-2">
          {primary.name && <div className="text-text">{primary.name}</div>}
          {primary.email && (
            <div className="text-text-mute font-mono truncate" title={primary.email}>
              {primary.email}
            </div>
          )}
          {primary.phone && (
            <div className="text-text-mute font-mono">{primary.phone}</div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-line text-[11px] text-text-mute">
        <span>{customer.terms}</span>
        {lastContact ? (
          <span>Last contact: {lastContact}</span>
        ) : (
          <span className="italic opacity-70">no contact yet</span>
        )}
      </div>
    </button>
  );
}
